import React, { useState, useEffect } from 'react';
import { 
  FileSpreadsheet, FileText, Download, Building2, Layers, History, 
  Upload, CheckCircle2, AlertCircle, Sparkles, RefreshCw, X, Plus, 
  Trash2, ShieldCheck, ChevronRight, Calculator, Landmark, ArrowLeft,
  Copy, Check
} from 'lucide-react';
import { DigitalSignatureModal } from './DigitalSignatureModal';
import BudgetAmendmentFlowView from './BudgetAmendmentFlowView';

interface BudgetDraftingAgentViewProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  currentUserEmail?: string;
  currency?: string;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

interface Branch {
  id: string;
  name: string;
  code?: string;
  city?: string;
}

interface TemplateItem {
  id: string;
  name: string;
  type: 'docx' | 'xlsx';
  uploadedAt: string;
  fileSize?: number;
}

interface KekvItem {
  kekv: string;
  name: string;
  generalFund: number;
  specialFund: number;
  notes?: string;
}

interface DraftResult {
  docType: string;
  title: string;
  planYear: number;
  summary: string;
  xlsxUrl: string;
  docxUrl: string;
  pdfUrl?: string;
  xlsxFilename: string;
  docxFilename: string;
  pdfFilename?: string;
  rolesUsed: Array<{ id: string; name: string; domain: string }>;
  branches: Branch[];
  items: KekvItem[];
  totalGeneralFund: number;
  totalSpecialFund: number;
  totalAllFunds: number;
  complianceAudit: {
    standard: string;
    budgetCodeCompliance: boolean;
    order57Compliance: boolean;
    generalToSpecialRatio: string;
    inflationFactorUsed: string;
    notes: string[];
  };
  justificationText: string;
}

export default function BudgetDraftingAgentView({
  language,
  onBack,
  onGoHome,
  currentUserEmail,
  useEnterpriseContext,
  enterpriseName: initialEnterpriseName
}: BudgetDraftingAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const currentYear = new Date().getFullYear();
  const defaultPlanYear = currentYear + 1;

  // Operating Mode Tab: amendment_flow (changes from branch letter) vs request_plan (budget request and multi-year declarations)
  const [activeTab, setActiveTab] = useState<'amendment_flow' | 'request_plan'>('amendment_flow');
  const [signModalDoc, setSignModalDoc] = useState<{ title: string; url: string } | null>(null);

  // Form State
  const [docType, setDocType] = useState<'budget_request' | 'budget_declaration' | 'estimate_justification'>('budget_request');
  const [planYear, setPlanYear] = useState<number>(defaultPlanYear);
  const [taskDescription, setTaskDescription] = useState('');
  const [parentOrg, setParentOrg] = useState('Міністерство / Головний розпорядник бюджетних коштів');
  const [includeKnowledgeBase, setIncludeKnowledgeBase] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  // Attached files (branch proposals, spreadsheets, memos)
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  // Enterprise & Config State
  const [enterpriseName, setEnterpriseName] = useState(initialEnterpriseName || '');
  const [enterpriseEdrpou, setEnterpriseEdrpou] = useState('');
  const [branches, setBranches] = useState<Branch[]>([
    { id: 'b-head', name: 'Головне управління / Центральний апарат', code: '001', city: 'Київ' },
    { id: 'b-1', name: 'Західна регіональна філія', code: '002', city: 'Львів' },
    { id: 'b-2', name: 'Південна науково-дослідна філія', code: '003', city: 'Одеса' }
  ]);
  const [historyData, setHistoryData] = useState<Record<string, any>>({
    [String(defaultPlanYear - 2)]: { generalFund: 14200000, specialFund: 2800000, staffCount: 52 },
    [String(defaultPlanYear - 1)]: { generalFund: 16100000, specialFund: 3400000, staffCount: 54 },
    [String(defaultPlanYear)]: { generalFund: 18500000, specialFund: 3900000, staffCount: 56 }
  });
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(false);

  // New branch modal / inline input
  const [showAddBranch, setShowAddBranch] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [newBranchCity, setNewBranchCity] = useState('');
  const [newBranchCode, setNewBranchCode] = useState('');

  // Execution State
  const [isDrafting, setIsDrafting] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DraftResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);

  // Load Budget Config from Server
  const fetchBudgetData = async () => {
    setLoadingConfig(true);
    try {
      const res = await fetch('/api/enterprise/budget/data');
      if (res.ok) {
        const data = await res.json();
        if (data.enterpriseName) setEnterpriseName(data.enterpriseName);
        if (data.enterpriseEdrpou) setEnterpriseEdrpou(data.enterpriseEdrpou);
        if (data.parentOrganization) setParentOrg(data.parentOrganization);
        if (data.branches && data.branches.length > 0) setBranches(data.branches);
        if (data.history && Object.keys(data.history).length > 0) setHistoryData(data.history);
        if (data.templates) setTemplates(data.templates);
      }
    } catch (err) {
      console.warn('[fetchBudgetData] error:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  useEffect(() => {
    fetchBudgetData();
  }, []);

  // Handle Add Branch
  const handleAddBranch = async () => {
    if (!newBranchName.trim()) return;
    const newB: Branch = {
      id: `b-${Date.now()}`,
      name: newBranchName.trim(),
      code: newBranchCode.trim() || `00${branches.length + 1}`,
      city: newBranchCity.trim() || 'Україна'
    };
    const updated = [...branches, newB];
    setBranches(updated);
    setNewBranchName('');
    setNewBranchCity('');
    setNewBranchCode('');
    setShowAddBranch(false);

    // Save to server
    try {
      await fetch('/api/enterprise/budget/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ branches: updated })
      });
    } catch (e) {
      console.warn('Failed to save branch config:', e);
    }
  };

  const handleRemoveBranch = async (id: string) => {
    const updated = branches.filter(b => b.id !== id);
    setBranches(updated);
    try {
      await fetch('/api/enterprise/budget/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ branches: updated })
      });
    } catch (e) {
      console.warn('Failed to save branch config:', e);
    }
  };

  // Upload Custom Template (.docx / .xlsx)
  const handleUploadTemplate = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch('/api/enterprise/budget/template-upload', {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        if (data.templates) setTemplates(data.templates);
        if (data.template) setSelectedTemplateId(data.template.id);
      } else {
        const errData = await res.json();
        alert(errData.error || 'Помилка завантаження шаблону');
      }
    } catch (err: any) {
      alert(err.message || 'Помилка мережі');
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('Видалити цей шаблон розрахунків?')) return;
    try {
      const res = await fetch(`/api/enterprise/budget/template/${id}`, { method: 'DELETE' });
      if (res.ok) {
        const data = await res.json();
        if (data.templates) setTemplates(data.templates);
        if (selectedTemplateId === id) setSelectedTemplateId('');
      }
    } catch (err) {
      console.warn('Error deleting template:', err);
    }
  };

  // 1-Click Demo Fill
  const handleFillDemo = (presetIndex: number) => {
    if (presetIndex === 1) {
      setDocType('budget_request');
      setPlanYear(2027);
      setTaskDescription(
        'Скласти консолідований бюджетний запит на 2027 рік (форми БЗ-1, БЗ-2) з урахуванням пропозицій трьох філій:\n' +
        '1. Заробітна плата (2111) та нарахування (2120): врахувати підвищення окладів за ЄТС та штат 54 особи;\n' +
        '2. Комунальні послуги (2270): зростання тарифів на тепло та електроенергію на 12%, передбачити покриття 15% за рахунок спецфонду (плата за послуги);\n' +
        '3. Модернізація приладів та серверного обладнання (3110) для Західної та Південної філій на суму 1.8 млн грн загального та 1.2 млн грн спеціального фонду.'
      );
    } else if (presetIndex === 2) {
      setDocType('estimate_justification');
      setPlanYear(2027);
      setTaskDescription(
        'Підготувати детальні розрахунки-обґрунтування до проєкту кошторису за Наказом Мінфіну № 57:\n' +
        '1. Розрахунок фонду оплати праці з обов\'язковими виплатами (вислуга років, надбавка за напруженість, оздоровчі);\n' +
        '2. Розрахунок комунальних послуг за фізичними обсягами споживання (Гкал, куб.м, кВт·год) та тарифами 2026/2027 рр.;\n' +
        '3. Обґрунтувати розподіл між загальним фондом (85%) та спецфондом (15% від оренди та надання науково-технічних послуг).'
      );
    } else {
      setDocType('budget_declaration');
      setPlanYear(2027);
      setTaskDescription(
        'Скласти середньострокову Бюджетну декларацію та Бюджетну пропозицію на 2027–2029 роки відповідно до статей 33-34 БКУ:\n' +
        '1. Ретроспективний аналіз факт 2024-2025 рр. та очікуване виконання 2026 р.;\n' +
        '2. Прогноз потреб на 2027 (базовий), 2028 (+8.5% інфляція) та 2029 (+7.5% інфляція) роки;\n' +
        '3. Виділити пріоритетні напрями розвитку регіональних філій та енергоефективні заходи.'
      );
    }
  };

  // Submit Drafting
  const handleStartDrafting = async () => {
    if (!taskDescription.trim()) {
      setError(isUk ? 'Будь ласка, опишіть завдання або оберіть готовий приклад.' : 'Please enter task description.');
      return;
    }

    setIsDrafting(true);
    setError(null);
    setResult(null);
    setLoadingStep(0);

    const stepInterval = setInterval(() => {
      setLoadingStep(prev => (prev < 4 ? prev + 1 : prev));
    }, 4500);

    const formData = new FormData();
    formData.append('docType', docType);
    formData.append('planYear', String(planYear));
    formData.append('taskDescription', taskDescription);
    formData.append('parentOrganization', parentOrg);
    formData.append('language', language);
    formData.append('includeKnowledgeBase', String(includeKnowledgeBase));
    if (selectedTemplateId) formData.append('customTemplateId', selectedTemplateId);
    formData.append('branchesJson', JSON.stringify(branches));
    formData.append('historyJson', JSON.stringify(historyData));

    for (const f of attachedFiles) {
      formData.append('files', f);
    }

    try {
      const res = await fetch('/api/enterprise/budget/draft', {
        method: 'POST',
        body: formData
      });

      clearInterval(stepInterval);

      if (!res.ok) {
        const errJson = await res.json();
        throw new Error(errJson.error || 'Помилка під час складання бюджетного документу.');
      }

      const data = await res.json();
      setResult(data.result);
    } catch (err: any) {
      clearInterval(stepInterval);
      setError(err.message || 'Не вдалося створити документ.');
    } finally {
      setIsDrafting(false);
    }
  };

  const stepsList = [
    'Зведення пропозицій філій та консолідація потреб підрозділів...',
    'Аналіз КЕКВ (Наказ № 333) та розподіл між Загальним і Спецфондом...',
    'Ретроспективний аудит та порівняння з попередніми роками...',
    'Формування аналітичного обґрунтування та пояснювальної записки...',
    'Генерація консолідованого Excel (.xlsx) та офіційного Word (.docx)...'
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8">
      {/* Top Navigation */}
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2.5 rounded-xl bg-slate-900/90 border border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800 transition-all cursor-pointer flex items-center gap-2 text-xs font-bold"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isUk ? 'Назад' : 'Back'}</span>
          </button>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-black uppercase tracking-wider flex items-center gap-1.5">
              <Landmark className="w-3.5 h-3.5" />
              <span>Публічні Фінанси & ДКСУ</span>
            </span>
            <span className="px-2.5 py-1 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-mono">
              БКУ • Наказ Мінфіну № 604 • Наказ № 57
            </span>
          </div>
        </div>

        {onGoHome && (
          <button
            onClick={onGoHome}
            className="text-xs font-bold text-slate-400 hover:text-emerald-400 transition-colors cursor-pointer"
          >
            {isUk ? 'На головну' : 'Home'}
          </button>
        )}
      </div>

      {/* Main Header Banner */}
      <div className="max-w-7xl mx-auto mb-8 bg-gradient-to-r from-slate-900 via-blue-950/40 to-slate-900 border border-blue-500/30 rounded-2xl p-6 shadow-2xl relative overflow-hidden">
        <div className="absolute -right-10 -bottom-10 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider bg-blue-500/20 text-blue-300 border border-blue-500/40">
                Корпоративний Фінансовий Орган
              </span>
              <span className="text-xs text-slate-400 font-mono">
                {enterpriseName || 'Юридична особа'} {enterpriseEdrpou ? `(ЄДРПОУ: ${enterpriseEdrpou})` : ''}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-white flex items-center gap-3">
              <Calculator className="w-7 h-7 text-blue-400" />
              <span>Конструктор Бюджетних Запитів, Декларацій та Кошторисів</span>
            </h1>
            <p className="text-sm text-slate-300 mt-2 max-w-3xl leading-relaxed">
              Автоматичне зведення пропозицій відокремлених підрозділів (філій), розподіл видатків між Загальним та Спеціальним фондами, 
              історична пам'ять за попередні роки та генерація детальних розрахунків-обґрунтувань за Наказом Мінфіну № 57 у форматі Excel (.xlsx) та Word (.docx).
            </p>
          </div>

          {/* KPI Mini-Cards */}
          <div className="flex flex-wrap gap-3">
            <div className="bg-slate-950/80 border border-slate-800 rounded-xl px-4 py-2.5 text-center min-w-[110px]">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Філій в обліку</div>
              <div className="text-lg font-black text-emerald-400 mt-0.5">{branches.length}</div>
            </div>
            <div className="bg-slate-950/80 border border-slate-800 rounded-xl px-4 py-2.5 text-center min-w-[110px]">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Фонди</div>
              <div className="text-xs font-black text-blue-300 mt-1">Загальний + Спец</div>
            </div>
            <div className="bg-slate-950/80 border border-slate-800 rounded-xl px-4 py-2.5 text-center min-w-[110px]">
              <div className="text-[10px] text-slate-400 font-bold uppercase">Пам'ять років</div>
              <div className="text-xs font-black text-amber-300 mt-1">t-2, t-1, t, t+1</div>
            </div>
          </div>
        </div>
      </div>

      {/* Operating Mode Selector Tabs */}
      <div className="max-w-7xl mx-auto mb-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setActiveTab('amendment_flow')}
          className={`px-5 py-3 rounded-2xl font-black text-xs md:text-sm flex items-center gap-2.5 transition-all shadow-md cursor-pointer border ${
            activeTab === 'amendment_flow'
              ? 'bg-gradient-to-r from-emerald-600 via-teal-600 to-blue-600 text-slate-950 border-emerald-400/50 shadow-emerald-500/20'
              : 'bg-slate-900/90 text-slate-300 hover:text-white border-slate-800 hover:border-slate-700'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          <span>🔄 Зміни до кошторису за листом філії (Філія ➔ НЦУВКЗ ➔ ДКА)</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-950/40 text-emerald-300">
            СЕДО • Наказ № 57
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('request_plan')}
          className={`px-5 py-3 rounded-2xl font-black text-xs md:text-sm flex items-center gap-2.5 transition-all shadow-md cursor-pointer border ${
            activeTab === 'request_plan'
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-blue-400/50 shadow-blue-500/20'
              : 'bg-slate-900/90 text-slate-300 hover:text-white border-slate-800 hover:border-slate-700'
          }`}
        >
          <Calculator className="w-4 h-4" />
          <span>📊 Зведений Бюджетний Запит & Декларація (БЗ-1, БЗ-2, Кошторис)</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-950/40 text-blue-300">
            Мінфін № 604
          </span>
        </button>
      </div>

      {activeTab === 'amendment_flow' ? (
        <div className="max-w-7xl mx-auto">
          <BudgetAmendmentFlowView
            language={language}
            enterpriseName={enterpriseName}
            enterpriseEdrpou={enterpriseEdrpou}
            branches={branches}
            parentOrg={parentOrg}
            currentUserEmail={currentUserEmail}
            onOpenSignModal={(title, url) => {
              setSignModalDoc({ title, url });
              setShowSignModal(true);
            }}
          />
        </div>
      ) : (
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column: Form Controls (7 cols) */}
          <div className="lg:col-span-7 space-y-6">

            {/* 1. Document Type & Planning Year */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 shadow-lg">
            <label className="block text-xs font-black uppercase tracking-wider text-slate-300 mb-3 flex items-center justify-between">
              <span>1. Тип бюджетного документа та плановий рік</span>
              <span className="text-blue-400 text-xs font-mono">БКУ ст. 33-35</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <button
                type="button"
                onClick={() => setDocType('budget_request')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  docType === 'budget_request'
                    ? 'bg-blue-600/20 border-blue-500 text-white shadow-md'
                    : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700'
                }`}
              >
                <div className="text-xs font-black flex items-center gap-1.5 text-blue-400">
                  <FileText className="w-4 h-4" />
                  <span>Бюджетний запит</span>
                </div>
                <div className="text-[11px] text-slate-400 mt-1">Форми БЗ-1, БЗ-2 за Інструкцією № 604</div>
              </button>

              <button
                type="button"
                onClick={() => setDocType('estimate_justification')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  docType === 'estimate_justification'
                    ? 'bg-emerald-600/20 border-emerald-500 text-white shadow-md'
                    : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700'
                }`}
              >
                <div className="text-xs font-black flex items-center gap-1.5 text-emerald-400">
                  <Calculator className="w-4 h-4" />
                  <span>Розрахунки до кошторису</span>
                </div>
                <div className="text-[11px] text-slate-400 mt-1">ЗП, 2270, послуги за Наказом № 57</div>
              </button>

              <button
                type="button"
                onClick={() => setDocType('budget_declaration')}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  docType === 'budget_declaration'
                    ? 'bg-purple-600/20 border-purple-500 text-white shadow-md'
                    : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700'
                }`}
              >
                <div className="text-xs font-black flex items-center gap-1.5 text-purple-400">
                  <History className="w-4 h-4" />
                  <span>Бюджетна декларація</span>
                </div>
                <div className="text-[11px] text-slate-400 mt-1">Середньостроковий прогноз на 3 роки</div>
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">
                  Плановий рік бюджету
                </label>
                <select
                  value={planYear}
                  onChange={(e) => setPlanYear(Number(e.target.value))}
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white font-bold focus:outline-none focus:border-blue-500"
                >
                  <option value={currentYear + 1}>{currentYear + 1} рік (Плановий наступний)</option>
                  <option value={currentYear}>{currentYear} рік (Поточний рік)</option>
                  <option value={currentYear + 2}>{currentYear + 2} рік (Середньостроковий)</option>
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">
                  Головний розпорядник коштів
                </label>
                <input
                  type="text"
                  value={parentOrg}
                  onChange={(e) => setParentOrg(e.target.value)}
                  placeholder="Міністерство освіти і науки України..."
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* 2. Branches & Consolidation Structure */}
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="block text-xs font-black uppercase tracking-wider text-slate-300">
                  2. Структура відокремлених підрозділів (філій)
                </span>
                <span className="text-[11px] text-slate-400">
                  Агент зведе індивідуальні потреби кожної філії в єдиний зведений документ
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowAddBranch(!showAddBranch)}
                className="px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/40 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Додати філію</span>
              </button>
            </div>

            {/* Inline Add Branch Form */}
            {showAddBranch && (
              <div className="bg-slate-950 border border-blue-500/40 rounded-xl p-3 mb-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input
                  type="text"
                  placeholder="Назва філії (напр., Полтавське відділення)"
                  value={newBranchName}
                  onChange={(e) => setNewBranchName(e.target.value)}
                  className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-white"
                />
                <input
                  type="text"
                  placeholder="Місто (напр., Полтава)"
                  value={newBranchCity}
                  onChange={(e) => setNewBranchCity(e.target.value)}
                  className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-white"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Код (004)"
                    value={newBranchCode}
                    onChange={(e) => setNewBranchCode(e.target.value)}
                    className="w-20 bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-white"
                  />
                  <button
                    type="button"
                    onClick={handleAddBranch}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-slate-950 font-bold text-xs rounded-lg cursor-pointer"
                  >
                    Зберегти
                  </button>
                </div>
              </div>
            )}

            {/* Branches List */}
            <div className="space-y-2">
              {branches.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center justify-between p-2.5 bg-slate-950/70 border border-slate-800/80 rounded-xl text-xs"
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-emerald-400" />
                    <span className="font-bold text-white">{b.name}</span>
                    <span className="text-slate-400 font-mono">({b.city || 'Україна'})</span>
                    <span className="px-1.5 py-0.5 bg-slate-800 text-slate-400 text-[10px] rounded font-mono">
                      Код: {b.code || '—'}
                    </span>
                  </div>
                  {branches.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveBranch(b.id)}
                      className="text-slate-500 hover:text-red-400 p-1 transition-colors cursor-pointer"
                      title="Видалити філію"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 3. Task Description & Presets */}
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-black uppercase tracking-wider text-slate-300">
                3. Завдання, ліміти та вимоги до бюджету
              </label>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-slate-400 font-bold uppercase">Демо-приклади:</span>
                <button
                  type="button"
                  onClick={() => handleFillDemo(1)}
                  className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 text-[11px] font-bold transition-all cursor-pointer"
                >
                  Запит філій
                </button>
                <button
                  type="button"
                  onClick={() => handleFillDemo(2)}
                  className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 text-[11px] font-bold transition-all cursor-pointer"
                >
                  Обґрунтування №57
                </button>
                <button
                  type="button"
                  onClick={() => handleFillDemo(3)}
                  className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 text-[11px] font-bold transition-all cursor-pointer"
                >
                  3-річна декларація
                </button>
              </div>
            </div>

            <textarea
              rows={5}
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              placeholder="Опишіть завдання: планові ліміти від головного розпорядника, необхідні закупівлі для кожної філії, прогнозовані тарифи на енергоносії, зміну окладів чи штатної чисельності..."
              className="w-full bg-slate-950 border border-slate-700 rounded-xl p-3 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none font-sans"
            />
          </div>

          {/* 4. Multi-File Upload (Branch Proposals & Custom Templates) */}
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 shadow-lg space-y-4">
            <div>
              <span className="block text-xs font-black uppercase tracking-wider text-slate-300 mb-1">
                4. Підвантаження пропозицій філій та зразків форм (Word / Excel)
              </span>
              <p className="text-[11px] text-slate-400">
                Завантажте файли пропозицій від філій (.docx, .xlsx, .pdf) та за потреби ваш власний зразок форми розрахунку.
              </p>
            </div>

            {/* Drag and Drop Zone */}
            <div className="border-2 border-dashed border-slate-700 hover:border-blue-500 rounded-xl p-4 text-center transition-all bg-slate-950/40">
              <input
                type="file"
                multiple
                accept=".docx,.xlsx,.xls,.pdf,.txt,.csv"
                id="budget-files-upload"
                onChange={(e) => {
                  if (e.target.files) {
                    setAttachedFiles(prev => [...prev, ...Array.from(e.target.files!)]);
                  }
                }}
                className="hidden"
              />
              <label htmlFor="budget-files-upload" className="cursor-pointer block">
                <Upload className="w-6 h-6 text-blue-400 mx-auto mb-1" />
                <span className="text-xs font-bold text-slate-200">
                  Натисніть для вибору файлів пропозицій філій (.docx, .xlsx, .pdf)
                </span>
              </label>
            </div>

            {/* Attached Files List */}
            {attachedFiles.length > 0 && (
              <div className="space-y-1.5">
                {attachedFiles.map((file, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 bg-slate-950 border border-slate-800 rounded-lg text-xs"
                  >
                    <div className="flex items-center gap-2 truncate">
                      {file.name.endsWith('.xlsx') ? (
                        <FileSpreadsheet className="w-4 h-4 text-emerald-400 shrink-0" />
                      ) : (
                        <FileText className="w-4 h-4 text-blue-400 shrink-0" />
                      )}
                      <span className="font-mono text-slate-200 truncate">{file.name}</span>
                      <span className="text-[10px] text-slate-500 shrink-0">
                        ({(file.size / 1024).toFixed(0)} KB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAttachedFiles(prev => prev.filter((_, i) => i !== idx))}
                      className="text-slate-500 hover:text-red-400 p-1 cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Custom Template Upload & Selector */}
            <div className="pt-2 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-300">Корпоративний зразок форми:</span>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  className="bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-xs text-slate-200"
                >
                  <option value="">Еталонні форми Мінфіну (Наказ № 604 / № 57)</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>Шаблон: {t.name}</option>
                  ))}
                </select>
              </div>

              <label className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5">
                <Plus className="w-3 h-3" />
                <span>+ Завантажити зразок (.xlsx/.docx)</span>
                <input
                  type="file"
                  accept=".docx,.xlsx"
                  onChange={handleUploadTemplate}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Options & Action Button */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
            <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-300 select-none">
              <input
                type="checkbox"
                checked={includeKnowledgeBase}
                onChange={(e) => setIncludeKnowledgeBase(e.target.checked)}
                className="rounded border-slate-700 text-blue-600 focus:ring-blue-500 bg-slate-900"
              />
              <span>Враховувати внутрішні положення з Бази Знань Підприємства</span>
            </label>

            <button
              type="button"
              disabled={isDrafting}
              onClick={handleStartDrafting}
              className={`px-6 py-3.5 rounded-xl font-black text-sm flex items-center gap-2.5 shadow-xl transition-all cursor-pointer ${
                isDrafting
                  ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-600 hover:from-blue-500 hover:to-emerald-500 text-white shadow-blue-600/30'
              }`}
            >
              {isDrafting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin text-blue-400" />
                  <span>Формування розрахунків...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 text-amber-300" />
                  <span>Сформувати Бюджетний Запит & Кошторис</span>
                </>
              )}
            </button>
          </div>

          {/* Loading Progress State */}
          {isDrafting && (
            <div className="bg-slate-900 border border-blue-500/40 rounded-2xl p-5 shadow-2xl">
              <div className="flex items-center justify-between text-xs text-blue-300 font-bold mb-3">
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-blue-400" />
                  <span>Комісія публічних фінансів працює...</span>
                </span>
                <span>Крок {loadingStep + 1} з {stepsList.length}</span>
              </div>
              <div className="w-full bg-slate-950 rounded-full h-2 mb-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-emerald-500 h-full transition-all duration-700"
                  style={{ width: `${((loadingStep + 1) / stepsList.length) * 100}%` }}
                />
              </div>
              <p className="text-xs text-slate-300 font-medium animate-pulse">
                {stepsList[loadingStep]}
              </p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-950/80 border border-red-500/50 rounded-xl text-red-200 text-xs flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              <div>{error}</div>
            </div>
          )}

        </div>

        {/* Right Column: Historical Memory & Results (5 cols) */}
        <div className="lg:col-span-5 space-y-6">

          {/* Historical Memory Card */}
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-black uppercase tracking-wider text-slate-200">
                  Історична пам'ять за попередні роки
                </span>
              </div>
              <span className="text-[10px] text-amber-300/80 font-mono">База порівняння</span>
            </div>
            <p className="text-[11px] text-slate-400 mb-3">
              Агент автоматично використовує ці фактичні обсяги для розрахунку темпів зростання та аргументації перед головним розпорядником.
            </p>

            <div className="space-y-2">
              {Object.entries(historyData).map(([yr, rec]: [string, any]) => (
                <div key={yr} className="p-2.5 bg-slate-950 border border-slate-800/80 rounded-xl flex items-center justify-between text-xs">
                  <div>
                    <span className="font-black text-amber-400">{yr} рік</span>
                    <span className="text-[10px] text-slate-400 ml-2">Штат: {rec.staffCount || 50} осіб</span>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-slate-200 font-bold">
                      Заг: {(Number(rec.generalFund || 0) / 1000000).toFixed(2)} млн грн
                    </div>
                    <div className="font-mono text-[10px] text-blue-400">
                      Спец: {(Number(rec.specialFund || 0) / 1000000).toFixed(2)} млн грн
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Results Card */}
          {result && (
            <div className="bg-slate-900 border border-emerald-500/40 rounded-2xl p-6 shadow-2xl space-y-5 animate-fadeIn">
              <div className="flex items-center justify-between">
                <span className="px-3 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-xs font-black uppercase tracking-wider flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>Документ успішно складено</span>
                </span>
                <span className="text-xs font-mono text-slate-400">
                  {result.planYear} рік
                </span>
              </div>

              <div>
                <h3 className="text-base font-bold text-white">{result.title}</h3>
                <p className="text-xs text-slate-300 mt-1 leading-relaxed">{result.summary}</p>
              </div>

              {/* Totals Banner */}
              <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-[10px] text-slate-400 font-bold uppercase">Загальний фонд</div>
                  <div className="text-sm font-black text-emerald-400 mt-0.5">
                    {result.totalGeneralFund.toLocaleString('uk-UA')} грн
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-400 font-bold uppercase">Спеціальний фонд</div>
                  <div className="text-sm font-black text-blue-400 mt-0.5">
                    {result.totalSpecialFund.toLocaleString('uk-UA')} грн
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-400 font-bold uppercase">ВСЬОГО (РАЗОМ)</div>
                  <div className="text-sm font-black text-amber-300 mt-0.5">
                    {result.totalAllFunds.toLocaleString('uk-UA')} грн
                  </div>
                </div>
              </div>

              {/* Compliance Badges */}
              <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-1 text-xs">
                <div className="text-[10px] text-slate-400 font-bold uppercase">Аудит відповідності:</div>
                <div className="flex items-center gap-2 text-slate-300">
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Бюджетний кодекс України: 100% комплаєнс</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Форми Наказу Мінфіну № 57: дотримано</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Співвідношення фондів: {result.complianceAudit.generalToSpecialRatio}</span>
                </div>
              </div>

              {/* Download Buttons */}
              <div className="space-y-2 pt-2">
                <a
                  href={result.xlsxUrl}
                  download={result.xlsxFilename}
                  className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-500 text-slate-950 font-bold text-xs rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  <span>Завантажити Зведений Excel (.xlsx з формулами)</span>
                </a>

                <a
                  href={result.docxUrl}
                  download={result.docxFilename}
                  className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <FileText className="w-4 h-4" />
                  <span>Завантажити Офіційний Word (.docx за ДСТУ)</span>
                </a>

                {result.pdfUrl && (
                  <a
                    href={result.pdfUrl}
                    download={result.pdfFilename}
                    className="w-full py-2 px-4 bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Завантажити PDF-версію для друку</span>
                  </a>
                )}

                <button
                  type="button"
                  onClick={() => setShowSignModal(true)}
                  className="w-full py-2 px-4 bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/40 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <ShieldCheck className="w-4 h-4 text-purple-400" />
                  <span>Накласти Електронний Підпис (КЕП / Дія.Підпис)</span>
                </button>
              </div>

            </div>
          )}

        </div>
      </div>
      )}

      {/* Digital Signature Modal */}
      {showSignModal && (result || signModalDoc) && (
        <DigitalSignatureModal
          isOpen={showSignModal}
          onClose={() => {
            setShowSignModal(false);
            setSignModalDoc(null);
          }}
          documentTitle={signModalDoc?.title || result?.title || "Бюджетний документ"}
          documentUrl={signModalDoc?.url || result?.pdfUrl || result?.docxUrl || ""}
          currentUserEmail={currentUserEmail}
        />
      )}

    </div>
  );
}
